<template>
  <div
    v-if="!loading"
    class="questionnaire-container"
  >
    <mastery-heading />
    <questionnaire
      v-if="startedQuestionnaire"
      :clipped-questions="clippedQuestions"
      :step="step"
      @answer-date-question="answerDateQuestion"
      @answer-question="answerQuestion"
      @back-clicked="goBack"
    >
      <template #completed>
        <div class="box">
          <div
            v-if="questionnaire && questionnaire.summaryHtml"
            v-html="questionnaire.summaryHtml"
          />
          <template v-else>
            <div class="title">
              {{ user && user.firstName ? `${user.firstName}'s` : "Your" }} Story
            </div>
            <div class="results">
              <template v-for="(result, index2) in results" :key="index2">
                <div
                  class="result"
                  v-html="result.rationale"
                />
                <div
                  v-if="result.value instanceof Array"
                  class="answer-array"
                >
                  <div
                    v-for="(answer, index3) in result.value"
                    :key="`answer-${index3}`"
                  >
                    <div
                      class="answer"
                      v-html="answer"
                    />
                  </div>
                </div>
              </template>
            </div>
          </template>
          <mastery-button
            class="begin"
            @mastery-button-clicked="summaryBeginClicked"
          >
            BEGIN
          </mastery-button>
        </div>
      </template>
    </questionnaire>
    <div
      v-else
      class="welcome-content"
    >
      <div
        v-if="!welcomeDetails"
        class="title"
      >
        Welcome, {{ user && user.firstName }}!
      </div>
      <div
        v-if="welcomeDetails"
        class="welcome-details"
        v-html="welcomeDetails"
      />
      <div
        v-else
        class="welcome-details fallback"
      >
        <p>Before you start, we have a few questions to help personalize your experience.</p>
        <p>This should take less than a minute.</p>
      </div>
      <mastery-button
        class="begin"
        @mastery-button-clicked="begin"
      >
        BEGIN
      </mastery-button>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns';

import MasteryButton from '@components/ui/masteryButton.vue';
import MasteryHeading from '@components/mastery/masteryHeading.vue';
import Questionnaire from '@/components/questionnaire/questionnaire.vue';

import QuestionnaireMixin from '@/mixins/questionnaire';

import { parseApiDateString } from '@/utils';
import {
  shouldEnterOnboardingQuestionnaire,
  shouldEnterTransitionQuestionnaire,
  shouldEnterQuestionnaire,
  onboardingQuestionnaireId,
  transitionQuestionnaireId,
} from '@/utils/questionnaire';
import {
  setupChecklist,
  ONBOARDING_CHECKLIST_KEY,
  setupTransitionChecklists,
} from '@/utils/checklist';

export default {
  name: 'OnboardingQuestionnaire',
  components: { MasteryButton, MasteryHeading, Questionnaire },
  mixins: [QuestionnaireMixin],
  data() {
    return {
      loading: true,
      oldClassification: null,
      storedQuestionnaireId: undefined,
    };
  },
  computed: {
    isOnboardingQuestionnaire() {
      return shouldEnterOnboardingQuestionnaire(this.$store, this.$apptimize);
    },
    isTransitionQuestionnaire() {
      return shouldEnterTransitionQuestionnaire(this.$store);
    },
    questionnaire() {
      let qId = this.questionnaireId || this.storedQuestionnaireId;
      if (qId) {
        return this.$store.getters['questionnaires/get'][qId];
      }
    },
    questionnaireId() {
      if (this.isOnboardingQuestionnaire) {
        return onboardingQuestionnaireId(this.$store, this.$apptimize);
      } else if (this.isTransitionQuestionnaire) {
        return transitionQuestionnaireId(this.$store);
      }
    },
    results() {
      return this.state
        .filter((s) => s.response)
        .map((state) => {
          const result = {};
          const value = state.response;
          const question = this.$store.getters['flashcards/flashcards'][
            state.questionId
          ];
          const datepicker = question.type === 'Date Picker';
          const multiline =
            question.answers.filter((a) => a.correct).length > 1;
          if (multiline) {
            result.rationale = question.rationale;
            result.value = JSON.parse(value);
          } else if (datepicker) {
            let date = format(
              parseApiDateString(value).date,
              JSON.parse(question.answers[0].text)['date_format'],
            );
            result.rationale = question.rationale.replace('{answer}', date);
          } else {
            result.rationale = question.answers.find(
              (a) => a.text === value,
            ).rationale;
            result.value = value;
          }

          // if there is no rationale for a single answer question
          if (!result.rationale) return;
          return result;
        })
        .filter((r) => r);
    },
    showPostTransitionMessage() {
      // make sure user has transitioned to show message
      const classificationId = this.$store.getters['session/classification']?.id;
      const hasTransitioned = classificationId !== this.oldClassification?.id;
      const transQuesitonnaireId = transitionQuestionnaireId(this.$store, this.oldClassification);
      const isTransitionQuestionnaire = this.storedQuestionnaireId == transQuesitonnaireId;
      return isTransitionQuestionnaire && hasTransitioned;
    },
    user() {
      return this.$store.getters['user/get'];
    },
    welcomeDetails() {
      if (this.questionnaire?.welcomeHtml) {
        return this.questionnaire.welcomeHtml;
      }

      const pv = this.$store.getters['productVars/get'][
        'questionnaireWelcomeDetails'
      ];
      return pv && pv.value;
    },
  },
  watch: {
    '$route.params.step': {
      handler: function (newVal, oldVal) {
        if (newVal < this.step) {
          // if user skips back, resubmit state as if they just answered 2 questions ago
          // this.back = true;
          const data = {};
          const last = this.state[this.step - 2];
          if (last) {
            data[last.questionId] = last.response;
          }
          if (this.clippedQuestions[1].id === 'completed') {
            this.$analytics.events.selectedPreviousFromOnboardingSummary();
          } else {
            this.$analytics.events.selectedPreviousFromOnboardingQuestion({
              'Flashcard Id': this.clippedQuestions[1].id,
              Stem: this.clippedQuestions[1].question,
            });
          }
          this.updateQuestionnaire(data, true).then(() => {
            this.goToNextQuestion(this.step - 1);
          });
        } else if (newVal > this.step) {
          // don't let user skip forward
          this.$router.push({
            name: 'mastery#questionnaire',
            params: { step: this.step },
          });
        }
      },
    },
  },
  created() {
    this.oldClassification = this.$store.getters['session/classification'];
    const apptimizeClassification = this.$apptimize.checkExperiments({
      experimentName: 'Hide Onboarding',
      str: 'defaultClassification',
      defaultVal: '',
      matcher: /Hide Onboarding/,
    });
    const classifications = this.$store.getters['classifications/all'];
    const classification = classifications.find(c => c.name === apptimizeClassification);
    if (classification) {
      const params = { user: { classificationId: classification.id } };
      this.$store.dispatch('user/update', params).then(() => {
        this.$store.dispatch('purchaseReceipts/create').then(() => {
          this.$router.push({ name: 'home' });
        });
      });
    } else if (!shouldEnterQuestionnaire(this.$store, this.$apptimize)) {
      this.$router.push({ name: 'home' });
    } else if (this.questionnaireUserState) {
      if (this.isOnboardingQuestionnaire) {
        const data = this.restoreLastState();
        this.updateQuestionnaire(data).then((next) => {
          this.goToNextQuestion();
        });
      } else if (shouldEnterTransitionQuestionnaire(this.$store)) {
        const loadedFlashcards = this.$store.getters['flashcards/flashcards'][
          this.questionnaireUserState.lastDeliveredQuestionId
        ];
        if (!loadedFlashcards) {
          const data = this.restoreLastState();
          this.updateQuestionnaire(data).then((next) => {
            this.setInitialState();
            this.goToNextQuestion();
          });
        } else {
          this.setInitialState();
          this.goToNextQuestion();
        }
      }
    } else if (this.$route.params.step) {
      this.$router.push({ name: 'mastery#questionnaire' });
    } else {
      this.loading = false;
    }
  },
  methods: {
    answerQuestion(question, answers) {
      const params = this.handleQuestionAnswer(question, answers);
      const data = params[question.id];
      this.$analytics.events.answeredOnboardingQuestion({
        'Question ID': question.id,
        'Question Text': question.question,
        'Selected Answer IDs': answers.map((a) => a.id),
        'Selected Answers': typeof data === 'string' ? [data] : data,
      });

      this.updateAndRoute(params);
    },
    answerDateQuestion(question, date) {
      this.$analytics.events.answeredOnboardingQuestion({
        'Question ID': question.id,
        'Question Text': question.question,
      });
      const params = this.handleDateAnswer(question, date);
      this.updateAndRoute(params);
    },
    begin() {
      this.$analytics.events.selectedBeginFromOnboardingWelcome();
      this.updateQuestionnaire({}).then(() => {
        this.goToNextQuestion(1);
      });
    },
    goBack() {
      this.$router.push({
        name: 'mastery#questionnaire',
        params: { step: this.step - 1 },
      });
    },
    goToNextQuestion(step) {
      this.loading = false;
      this.updateStep(step);

      this.$router.push({
        name: this.$route.name,
        params: { step: this.step },
      });
    },
    summaryBeginClicked() {
      this.$analytics.events.selectedBeginFromOnboardingSummary();
      const route = {
        name: 'home',
      };
      const redirectAfterLogin = this.$cookies.get('redirectAfterLogin');

      // If a user has transitioned, it's important that they land on the home
      // dashboard to complete the post transition workflow. There usually isn't
      // be a time where showPostTransitionMessage and redirectAfterLogin are true
      // but to be sure, we'll make the transition supercede the redirectAfterLogin
      // behavior.
      if (this.showPostTransitionMessage) {
        setupTransitionChecklists(this.$store);
        route.query = {
          transition: true,
          classification: this.oldClassification.id,
        };
        this.$router.push(route);
      } else if (redirectAfterLogin) {
        this.$router.push(decodeURI(redirectAfterLogin)).then(() => {
          this.$cookies.remove('redirectAfterLogin');
        });
      } else {
        this.$router.push(route);
      }
    },
    updateAndRoute(params) {
      this.updateQuestionnaire(params).then((response) => {
        if (!response.nextQuestion) {
          this.$analytics.events.viewedOnboardingSummary();
          // make sure user class dimension is set
          this.$analytics.config(this.$store.getters);
          this.$apptimize.config(this.user);
          // create checklist state here
          setupChecklist(this.$store, ONBOARDING_CHECKLIST_KEY);
        }
        this.goToNextQuestion();
      });
    },
  },
};
</script>

<style lang='scss' scoped>
.questionnaire-container {
  background-color: white;
  background-image: url("../../assets/images/pattern2.svg");
  background-position: bottom;
  background-repeat: repeat no-repeat;
  text-align: center;

  .begin,
  :deep(.submit) {
    font-size: rem-calc(14);
    width: 80%;

    @include breakpoint(small) {
      margin-top: rem-calc(12);
      width: 90%;
    }
  }

  :deep(.submit) {
    margin-top: rem-calc(32);

    &.disabled {
      opacity: 0.6;
      pointer-events: none;
    }
  }

  .title {
    font-family: "CharterBold" !important;
    font-size: rem-calc(32);
    font-weight: $semi-bold;
  }

  .welcome-content {
    margin: rem-calc(160) auto 0 auto;
    max-width: rem-calc(450);
    width: 100%;

    @include breakpoint(small) {
      margin-top: rem-calc(96);
    }

    .welcome-details {
      font-size: rem-calc(24);
      line-height: initial;

      &.fallback {
        p:last-of-type {
          font-weight: $bold;
        }
      }
    }
  }

  :deep(.questionnaire-component-container) {
    display: flex;
    margin: 0 rem-calc(-48);
    min-height: calc(100% - #{rem-calc(64)});

    .content-container {
      overflow-x: hidden;
      padding: rem-calc(48) 0;

      .slide-move {
        transition: transform 1s;

        .back {
          display: none;
        }
      }

      .slide-leave-active {
        left: -170%;
        position: absolute;

        &.right {
          left: 0%;
          right: -170%;
        }
      }

      .slide-enter {
        transform: translateX(70%);
      }

      &.back {
        .slide-enter {
          transform: translateX(-70%);
        }

        .slide-leave-active {
          left: 170%;
        }
      }

      .content {
        text-align: center;
        transition: all 1s;

        @media only screen and (max-width: 1200px) {
          min-width: 67%;
        }

        @media only screen and (max-width: 824px) {
          margin: 0 rem-calc(160);
          min-width: 80%;
        }

        .back {
          right: 80%;
          top: 50%;

          @include breakpoint(small) {
            right: 48%;
            top: 0;
          }
        }

        .results {
          font-size: 16px;
          margin: rem-calc(16) auto;
          text-align: left;
          width: 85%;

          @include breakpoint(small) {
            width: 95%;
          }

          > .result {
            margin-top: rem-calc(16);

            p {
              margin: 0;
            }

            span {
              border-bottom: 2px solid #7EE8FA;
              font-weight: $semi-bold;
            }
          }

          .answer-array {
            padding-left: 8px;

            .answer {
              border-bottom: 2px solid #7EE8FA;
              display: inline-block;
              font-weight: $semi-bold;
              margin: 4px 0 0 4px;

              p {
                margin: 0;
              }
            }
          }
        }

        .box {
          align-items: center;
          background-color: white;
          box-shadow: 0 6px 12px 0 rgba(0, 0, 0, 0.4);
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          height: 100%;
          justify-content: center;
          margin: auto;
          max-width: rem-calc(500);
          min-height: 300px;
          padding: rem-calc(40) rem-calc(56);
          width: 100%;

          @include breakpoint(small) {
            min-height: 0px;
            padding: rem-calc(32) rem-calc(16);
          }

          &.empty {
            display: none;
          }

          .date-picker {
            margin-top: rem-calc(16);
          }

          .question {
            @include charter-font;
            font-size: rem-calc(32);

            p {
              @include charter-font;
            }
          }
        }
      }
    }
  }

  @media all and (-ms-high-contrast:none) {
    .content-container {
      .slide-move {
        transition: all 0s !important;
      }

      .slide-enter-active {
        display: none !important;
      }

      .content {
        transition: all 0s !important;

        &:not(:nth-child(2n)) {
          display: none !important;
        }

        .back {
          margin-right: 24px;
          top: 0% !important;
        }
      }
    }
  }
}
</style>
